import type { DriveStep } from 'driver.js';
import { getDashboardTourSteps } from './dashboardTour';
import { getStudioTourSteps } from './studioTour';
import { getCommunitiesTourSteps } from './communitiesTour';
import { getBlogTourSteps } from './blogTour';
import { getBrandTourSteps } from './brandTour';
import { getIntegrationsTourSteps } from './integrationsTour';
import { getGoLiveTourSteps } from './goLiveTour';

export interface PageTour {
  id: string;
  /** Button label. The dashboard's is "Take a tour"; per-page tours say "Tour this page". */
  label: string;
  /** If set, open the mobile slide-in sidebar before starting (for sidebar-targeted tours). */
  needsSidebar?: boolean;
  getSteps: () => DriveStep[];
}

export const DASHBOARD_TOUR_ID = 'dashboard';

// Module-level constants → stable references, so matchPageTour() returns the same object for a
// given route across renders (keeps the consuming useCallback stable).
// The dashboard tour now leads with the dashboard's own content, so it does NOT force-open the
// mobile sidebar (that would cover the page); its sidebar-targeted steps simply skip on mobile.
const DASHBOARD_TOUR: PageTour = {
  id: DASHBOARD_TOUR_ID,
  label: 'Take a tour',
  getSteps: getDashboardTourSteps,
};

// Exact pathname → tour. Keep keys in sync with the routes in App.tsx + the data-tour anchors
// in each page component. Adding a new page tour is just one entry here + a steps file + anchors.
const PAGE_TOURS: Record<string, PageTour> = {
  '/dashboard': DASHBOARD_TOUR,
  '/studio': { id: 'studio', label: 'Tour this page', getSteps: getStudioTourSteps },
  '/studio/communities': { id: 'communities', label: 'Tour this page', getSteps: getCommunitiesTourSteps },
  '/studio/blog': { id: 'blog', label: 'Tour this page', getSteps: getBlogTourSteps },
  '/brands': { id: 'brands', label: 'Tour this page', getSteps: getBrandTourSteps },
  '/integrations': { id: 'integrations', label: 'Tour this page', getSteps: getIntegrationsTourSteps },
  '/go-live': { id: 'go-live', label: 'Tour this page', getSteps: getGoLiveTourSteps },
};

/** The tour for a route, or null if that route has no tour (the "Take a tour" button hides). */
export function matchPageTour(pathname: string): PageTour | null {
  return PAGE_TOURS[pathname] ?? null;
}
